import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { venuesAPI } from '../api';
import { useAuth } from '../hooks/useAuth';
import { T, fonts } from '../theme';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';

export default function CheckInLanding() {
    const { user, loading: authLoading } = useAuth();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('v');
    
    const [venue, setVenue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checkingIn, setCheckingIn] = useState(false);
    const [status, setStatus] = useState('idle'); // idle | success | error
    const [errorMsg, setErrorMsg] = useState('');
    
    const navigate = useNavigate();

    useEffect(() => {
        if (token) {
            venuesAPI.verifyCheckin(token).then(res => {
                setVenue(res.data.venue);
            }).catch(err => {
                setErrorMsg(err.response?.data?.error || 'Invalid check-in token');
                setStatus('error');
            }).finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, [token]);

    const handleCheckIn = async () => {
        if (!user) {
            navigate('/login', { state: { from: `/checkin?v=${token}` } });
            return;
        }

        setCheckingIn(true);
        setStatus('idle');
        setErrorMsg('');

        if (!navigator.geolocation) {
            setErrorMsg('Geolocation is not supported by your browser');
            setStatus('error');
            setCheckingIn(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    await venuesAPI.submitCheckin(token, position.coords.latitude, position.coords.longitude);
                    setStatus('success');
                } catch (err) {
                    setErrorMsg(err.response?.data?.error || 'Check-in failed. Are you at the venue?');
                    setStatus('error');
                } finally {
                    setCheckingIn(false);
                }
            },
            (err) => {
                setErrorMsg('Location access denied. Please enable GPS to check in.');
                setStatus('error');
                setCheckingIn(false);
            }
        );
    };

    if (loading || authLoading) return <LoadingSpinner />;

    if (!token) {
        return (
            <div style={s.screen}>
                <div style={s.header}>
                    <h1 style={s.title}>Check-In</h1>
                    <p style={s.subtitle}>Please scan a valid venue QR code to continue</p>
                </div>
                <div style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: 60, marginBottom: 20 }}>🔍</div>
                    <p style={{ color: T.smoke, marginBottom: 20 }}>No venue token found.</p>
                    <button onClick={() => navigate('/checkin_manual')} style={s.scanBtn}>Open Manual Scanner</button>
                </div>
                <BottomNav active="/checkin_landing" />
            </div>
        );
    }

    return (
        <div style={s.screen}>
            {/* Dark Header */}
            <div style={s.header}>
                <h1 style={s.title}>Venue Check-In</h1>
                <p style={s.subtitle}>{status === 'success' ? 'Visit Logged!' : venue ? 'You are at:' : 'Verifying Venue...'}</p>
            </div>

            <div style={s.content}>
                {status === 'success' ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={s.successBadge}>✅</div>
                        <h2 style={{ fontFamily: fonts.heading, fontSize: 24, marginBottom: 10 }}>Badge Earned!</h2>
                        <p style={{ color: T.smoke, marginBottom: 24 }}>You've successfully checked into <strong>{venue?.name}</strong>.</p>
                        <button onClick={() => window.location.href = '/profile'} style={s.scanBtn}>View My Progress</button>
                    </div>
                ) : status === 'error' ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={s.errorBadge}>❌</div>
                        <h2 style={{ fontFamily: fonts.heading, fontSize: 24, marginBottom: 10 }}>Check-In Failed</h2>
                        <p style={{ color: T.danger, marginBottom: 24 }}>{errorMsg}</p>
                        <button onClick={handleCheckIn} style={s.scanBtn}>Try Again</button>
                    </div>
                ) : (
                    <>
                        <div style={s.qrCard}>
                            <div style={s.venueRow}>
                                <div style={s.venueIcon}>
                                    <span style={{ fontSize: 28 }}>{venue?.icon || '🥃'}</span>
                                </div>
                                <div style={s.venueInfo}>
                                    <div style={s.venueName}>{venue?.name || 'Loading Venue...'}</div>
                                    <div style={s.venueTagline}>{venue?.tagline}</div>
                                    <div style={s.venueCity}>
                                        {venue?.city}, <span style={s.venueState}>KY</span>
                                    </div>
                                </div>
                            </div>

                            {!user ? (
                                <div style={s.loginPrompt}>
                                    <div style={{ fontSize: 13, color: T.ink, fontWeight: 500, marginBottom: 12 }}>
                                        Sign in to log your visit and earn badges.
                                    </div>
                                    <button onClick={() => navigate('/login', { state: { from: `/checkin?v=${token}` } })} style={s.loginBtn}>
                                        Sign In to Check In
                                    </button>
                                </div>
                            ) : (
                                <div style={{ borderTop: `1px solid ${T.creamDark}`, paddingTop: 20 }}>
                                    <button 
                                        onClick={handleCheckIn} 
                                        disabled={checkingIn}
                                        style={s.scanBtn}
                                    >
                                        {checkingIn ? 'Verifying Location...' : '📍 Check In Here'}
                                    </button>
                                    <p style={{ textAlign: 'center', fontSize: 11, color: T.smoke, marginTop: 12 }}>
                                        Requires GPS to verify you are at the venue
                                    </p>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <BottomNav active="/checkin_landing" />
        </div>
    );
}

const s = {
    screen: { background: '#f0ebe3', minHeight: '100vh', paddingBottom: 100, fontFamily: fonts.body },
    header: { background: T.ink, padding: '56px 20px 28px', color: '#fff' },
    title: { fontSize: 26, fontFamily: fonts.heading, margin: 0, fontWeight: 'bold' },
    subtitle: { fontSize: 13, color: T.smoke, marginTop: 6 },
    content: { padding: 20 },
    qrCard: {
        background: '#fff', borderRadius: 20, padding: '24px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.06)'
    },
    venueRow: { display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 24 },
    venueIcon: {
        width: 60, height: 60, background: T.cream, borderRadius: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
    },
    venueInfo: { flex: 1 },
    venueName: { fontSize: 18, fontWeight: '800', color: T.ink },
    venueTagline: { fontSize: 13, color: T.smoke, marginTop: 4, lineHeight: 1.3 },
    venueCity: { fontSize: 13, color: T.copper, marginTop: 6, fontWeight: 600 },
    venueState: { fontWeight: '800' },
    
    loginPrompt: { textAlign: 'center', background: T.cream, padding: 20, borderRadius: 16 },
    loginBtn: {
        width: '100%', padding: '14px', background: T.ink, color: '#fff', 
        border: 'none', borderRadius: 40, fontWeight: '700', cursor: 'pointer'
    },
    scanBtn: {
        width: '100%', background: `linear-gradient(135deg, ${T.copper} 0%, ${T.copperDark} 100%)`,
        color: '#fff', border: 'none', borderRadius: 40, padding: '16px',
        fontSize: 16, fontWeight: '700', cursor: 'pointer',
        boxShadow: '0 6px 20px rgba(184,115,51,0.3)'
    },
    successBadge: {
        width: 80, height: 80, borderRadius: '50%', background: T.successBg, border: `2px solid ${T.success}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, margin: '20px auto'
    },
    errorBadge: {
        width: 80, height: 80, borderRadius: '50%', background: T.dangerBg, border: `2px solid ${T.danger}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, margin: '20px auto'
    }
};
